// src/components/ProtectedRoute.js
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';

function ProtectedRoute({ children, allowedRoles = [], requiredFunction = null, allowPublic = false }) {
  const { isAuthenticated, role, functions } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const isEsdCheckPage = location.pathname === "/esd-check";
  const isDeveloperLandingPage = location.pathname === "/developer/landing";
  const isFeedbackPage = location.pathname === "/developer/feedback";

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1);
  }, []);

  // Debug logging for monitoring the component's behavior
  useEffect(() => {
    console.log("ProtectedRoute - Current state:", {
      isAuthenticated,
      role,
      functions,
      location: location.pathname,
      allowedRoles,
      isEsdCheckPage,
      isDeveloperLandingPage,
      isFeedbackPage
    });
  }, [isAuthenticated, role, functions, location.pathname, allowedRoles, isEsdCheckPage, isDeveloperLandingPage, isFeedbackPage]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Check for public access first - if allowPublic is true, allow access regardless of authentication
  if (allowPublic) {
    return children;
  }

  // Always allow access to ESD check page and Developer Landing page (legacy support)
  if (isEsdCheckPage || isDeveloperLandingPage) {
    return children;
  }

  // Only redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log("Not authenticated, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Function to map function names to route paths - MUST MATCH the mapping in Sidebar.js
  const functionToRoutePath = (func) => {
    const functionRouteMap = {
      'dashboard': '/dashboard',
      'activities': '/activities',
      'stock': '/stock',
      'shipping': '/shipping',
      'social': '/social',
      'workers': '/workers',
      'repaircost': '/repair-cost',     
      'priceadmin': '/price-admin',     
      'settings': '/settings',
      'esdoverview': '/esd-overview',
      'systemdocumentation': '/developer/back', // Backend Docs
      'frontenddocs': '/developer/front',
      'backenddocs': '/developer/back',
      'webork-server': '/webork-server',
    };
    return functionRouteMap[func] || null;
  };

  // Find the first page the user has access to based on functions
  const getFirstAccessiblePage = () => {
    if (!functions || functions.length === 0) {
      return getHomePageForRole(role);
    }
    for (const func of functions) {
      const path = functionToRoutePath(func);
      if (path) {
        return path;
      }
    }
    return getHomePageForRole(role);
  };

  // Get the home page for the user based on their role (fallback)
  const getHomePageForRole = (userRole) => {
    let homePage;
    switch(userRole) {
      case 'repair-cost':
        homePage = '/repair-cost';
        break;
      case 'price-admin':
        homePage = '/repair-cost';
        break;
      case 'workers':
        homePage = '/workers';
        break;
      case 'developer':
        homePage = '/developer/front';
        break;
      case 'admin':
        homePage = '/dashboard';
        break;
      case 'superadmin':
        homePage = '/dashboard';
        break;
      default:
        homePage = '/dashboard';
    }
    return homePage;
  };

  // Check if the user has a function that matches the current route
  const hasFunctionForCurrentRoute = () => {
    if (!functions || functions.length === 0) return false;
    const currentPath = location.pathname;
    for (const func of functions) {
      const mappedPath = functionToRoutePath(func);
      if (mappedPath && mappedPath === currentPath) {
        return true;
      }
    }
    return false;
  };

  // Check for required function access
  if (requiredFunction && (!functions || !functions.includes(requiredFunction))) {
    console.log('Debug routing:', {
      requiredFunction,
      userFunctions: functions,
      currentPath: location.pathname,
      mappedPaths: functions ? functions.map(f => ({ 
        function: f, 
        path: functionToRoutePath(f) 
      })) : []
    });
    console.log(`Missing required function: ${requiredFunction}`);
    return <Navigate to={getFirstAccessiblePage()} />;
  }

  // Check if the current path is accessible based on the user's role-specific restrictions
  const isPathAllowedForRoleOrFunction = () => {
    // Superadmin has access to all pages
    if (role === 'superadmin') {
      return true;
    }
    if (role === 'repair-cost') {
      return location.pathname === '/repair-cost';
    }
    if (role === 'price-admin') {
      return location.pathname === '/repair-cost' || location.pathname === '/price-admin';
    }
    // If user has function for this route, allow
    if (hasFunctionForCurrentRoute()) {
      return true;
    }
    // For other roles, check against allowed roles
    if (allowedRoles.length > 0) {
      return allowedRoles.includes(role);
    }
    return true;
  };

  if (!isPathAllowedForRoleOrFunction()) {
    const accessiblePage = getFirstAccessiblePage();
    return <Navigate to={accessiblePage} />;
  }

  return children;
}

export default ProtectedRoute;
